import React, { useEffect, useState } from "react";
import AppShell from "../components/ui/AppShell";
import PageHeader from "../components/ui/PageHeader";
import API from "../api/api";
import { FaEdit, FaTrash, FaPlus, FaCopy, FaEye, FaPaperPlane } from "react-icons/fa";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";

const emptyForm = {
  service_type: "",
  login_email: "",
  login_password: "",
  login_url: "https://visa.vfsglobal.com/gbr/en/nld/login",
  application_centre: "",
  appointment_category: "",
  sub_category: "",
  first_name: "",
  last_name: "",
  gender: "",
  nationality: "",
  passport_number: "",
  passport_expiry: "",
  reason_for_travel: "",
  purpose_of_stay: "",
  date_of_birth: "",
  passport_type: "",
  departure_date: "",
  address: "",
  address_line_2: "",
  state: "",
  city: "",
  appointment_date: "",
  contact_number: "",
  card_holder_name: "",
  card_number: "",
  notes: "",
  assigned_user_id: "",
  status: "assign" // default status
};

const AssistedBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showView, setShowView] = useState(false);

  const [currentBooking, setCurrentBooking] = useState({});
  const [formData, setFormData] = useState(emptyForm);
  const [sendingBookingId, setSendingBookingId] = useState(null);
  const [sendingFormPayload, setSendingFormPayload] = useState(false);

  const isVFS = formData.service_type === "VFS";
  const isTLS = formData.service_type === "TLS";

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      const res = await API.get("/assisted-bookings");
      setBookings(res.data);
      setError("");
      setLoading(false);
    } catch {
      setError("Failed to fetch bookings");
      setLoading(false);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      const res = await API.get("/users");
      setUsers(res.data);
    } catch {
      console.error("Failed to fetch users");
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchUsers();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Add booking
  const handleAdd = async () => {
    try {
      await API.post("/assisted-bookings", formData);
      fetchBookings();
      setShowAdd(false);
      setFormData(emptyForm);
    } catch {
      alert("Failed to add booking");
    }
  };

  // Edit booking
  const handleEdit = async () => {
    try {
      await API.put(`/assisted-bookings/${currentBooking.id}`, formData);
      fetchBookings();
      setShowEdit(false);
    } catch {
      alert("Failed to update booking");
    }
  };

  // Delete booking
  const handleDelete = async () => {
    try {
      await API.delete(`/assisted-bookings/${currentBooking.id}`);
      fetchBookings();
      setShowDelete(false);
    } catch {
      alert("Failed to delete booking");
    }
  };

  // Copy single field
  const handleDoubleClickCopy = (value) => {
    if (!value) return;
    navigator.clipboard.writeText(value.toString());
    alert("Copied: " + value);
  };

  // Copy all fields
  const copyAllFields = () => {
    const text = Object.entries(formData)
      .map(([key, val]) => `${key}: ${val || ""}`)
      .join("\n");
    navigator.clipboard.writeText(text);
    alert("All fields copied!");
  };

  const handleSendToExtension = async (bookingId) => {
    try {
      setSendingBookingId(bookingId);
      const res = await API.post(`/extension-bridge/bookings/${bookingId}/push`);
      const delivered = res.data?.bridge?.delivered || 0;

      if (delivered > 0) {
        alert(`Booking sent to ${delivered} connected extension client(s). Open the extension popup and click Start.`);
      } else {
        alert("Booking payload prepared, but no extension client is currently connected to the websocket bridge.");
      }
    } catch (err) {
      const missing = err.response?.data?.missing;
      if (Array.isArray(missing) && missing.length > 0) {
        alert(`Cannot send booking. Missing required VFS fields: ${missing.join(", ")}`);
      } else {
        alert(err.response?.data?.message || "Failed to send booking to extension");
      }
    } finally {
      setSendingBookingId(null);
    }
  };

  const handleSendFormToExtension = async () => {
    try {
      setSendingFormPayload(true);
      const res = await API.post("/extension-bridge/push", formData);
      const delivered = res.data?.bridge?.delivered || 0;

      if (delivered > 0) {
        alert(`Frontend form sent to ${delivered} connected extension client(s). Open the extension popup and click Start.`);
      } else {
        alert("Frontend form was accepted by the backend, but no extension client is currently connected to the websocket bridge.");
      }
    } catch (err) {
      const missing = err.response?.data?.missing;
      if (Array.isArray(missing) && missing.length > 0) {
        alert(`Cannot send form. Missing required VFS fields: ${missing.join(", ")}`);
      } else {
        alert(err.response?.data?.message || "Failed to send frontend form to extension");
      }
    } finally {
      setSendingFormPayload(false);
    }
  };

  const inputProps = (name) => ({
    name,
    value: formData[name] || "",
    onChange: handleChange,
    onDoubleClick: () => handleDoubleClickCopy(formData[name]),
    style: { cursor: "copy" },
  });

  const renderDynamicForm = () => (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Service Type</Form.Label>
        <Form.Select {...inputProps("service_type")}>
          <option value="">Select Service</option>
          <option value="VFS">VFS Global</option>
          <option value="TLS">TLS</option>
        </Form.Select>
      </Form.Group>

      <Row>
        <Col>
          <Form.Control placeholder="Login Email" {...inputProps("login_email")} />
        </Col>
        <Col>
          <Form.Control
            type="password"
            placeholder="Login Password"
            {...inputProps("login_password")}
          />
        </Col>
      </Row>

      {isVFS && (
        <>
          <hr />
          <h6>VFS Booking Setup</h6>
          <Form.Control className="mb-3" placeholder="VFS Login URL" {...inputProps("login_url")} />

          <Row className="mb-3">
            <Col>
              <Form.Control placeholder="Application Centre" {...inputProps("application_centre")} />
            </Col>
            <Col>
              <Form.Control placeholder="Appointment Category" {...inputProps("appointment_category")} />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Control placeholder="Sub Category" {...inputProps("sub_category")} />
            </Col>
            <Col>
              <Form.Control type="date" {...inputProps("appointment_date")} />
            </Col>
          </Row>
        </>
      )}

      <hr />
      <h6>Applicant Details</h6>

      <Row className="mb-3">
        <Col>
          <Form.Control placeholder="First Name" {...inputProps("first_name")} />
        </Col>
        <Col>
          <Form.Control placeholder="Last Name" {...inputProps("last_name")} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form.Select {...inputProps("gender")}>
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </Form.Select>
        </Col>
        <Col>
          <Form.Control type="date" {...inputProps("date_of_birth")} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form.Control placeholder="Nationality" {...inputProps("nationality")} />
        </Col>
        <Col>
          <Form.Control placeholder="Passport Number" {...inputProps("passport_number")} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form.Control type="date" {...inputProps("passport_expiry")} />
        </Col>
        <Col>
          <Form.Control placeholder="Contact Number" {...inputProps("contact_number")} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form.Control placeholder="Address Line 1" {...inputProps("address")} />
        </Col>
        <Col>
          <Form.Control placeholder="Address Line 2" {...inputProps("address_line_2")} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Form.Control placeholder="State" {...inputProps("state")} />
        </Col>
        <Col>
          <Form.Control placeholder="City" {...inputProps("city")} />
        </Col>
      </Row>


      {isTLS && (
        <>
          <Form.Control placeholder="Reason for Travel" {...inputProps("reason_for_travel")} />
          <Form.Control placeholder="Purpose of Stay" {...inputProps("purpose_of_stay")} />
          <Form.Control placeholder="Passport Type" {...inputProps("passport_type")} />
          <Form.Control type="date" {...inputProps("departure_date")} />
        </>
      )}

      <hr />
      <h6>Payment (Demo Only)</h6>
      <Form.Control placeholder="Card Holder Name" {...inputProps("card_holder_name")} />
      <Form.Control placeholder="Card Number" {...inputProps("card_number")} />
      <Form.Control as="textarea" placeholder="Notes" {...inputProps("notes")} />

      {/* Assign User */}
      <Form.Group className="mb-3 mt-3">
        <Form.Label>Assign User</Form.Label>
        <Form.Select
          name="assigned_user_id"
          value={formData.assigned_user_id || ""}
          onChange={handleChange}
        >
          <option value="">Select User</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.username}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {/* Status */}
      <Form.Group className="mb-3">
        <Form.Label>Status</Form.Label>
        <Form.Select name="status" value={formData.status || "assign"} onChange={handleChange}>
          <option value="assign">Assign</option>
          <option value="booked">Booked</option>
        </Form.Select>
      </Form.Group>
    </>
  );

  return (
    <AppShell>
      <PageHeader
        title="Assisted Bookings"
        description="Create and manage assisted VFS/TLS booking assignments."
        actions={
          <Button variant="primary" onClick={() => setShowAdd(true)}>
            <FaPlus /> Add Booking
          </Button>
        }
      />

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="vp-loading">Loading…</div>
      ) : (
        <div className="vp-table-wrap table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Service</th>
                <th>Name</th>
                <th>Passport</th>
                <th>Contact</th>
                <th>Assigned User</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, i) => (
                <tr key={b.id}>
                  <td>{i + 1}</td>
                  <td>{b.service_type}</td>
                  <td>{b.first_name} {b.last_name}</td>
                  <td>{b.passport_number}</td>
                  <td>{b.contact_number}</td>
                  <td>{b.assigned_user_name || "Not Assigned"}</td>
                  <td>{b.status || "assign"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="info"
                      className="me-2"
                      title="View"
                      onClick={() => { setFormData(b); setShowView(true); }}
                    >
                      <FaEye />
                    </Button>

                    <Button
                      size="sm"
                      variant="success"
                      className="me-2"
                      title="Send to Extension"
                      disabled={sendingBookingId === b.id}
                      onClick={() => handleSendToExtension(b.id)}
                    >
                      <FaPaperPlane />
                    </Button>

                    <Button
                      size="sm"
                      variant="warning"
                      className="me-2"
                      onClick={() => { setCurrentBooking(b); setFormData(b); setShowEdit(true); }}
                    >
                      <FaEdit />
                    </Button>

                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => { setCurrentBooking(b); setShowDelete(true); }}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ADD MODAL */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Form>{renderDynamicForm()}</Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button
            variant="success"
            onClick={handleSendFormToExtension}
            disabled={sendingFormPayload}
          >
            Send to Extension
          </Button>
          <Button variant="primary" onClick={handleAdd}>Save</Button>
        </Modal.Footer>
      </Modal>

      {/* EDIT MODAL */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg" centered>
        <Modal.Header closeButton>
          <div className="d-flex w-100 align-items-center">
            <Modal.Title>Edit Booking</Modal.Title>
            <FaCopy
              title="Copy All Fields"
              onClick={copyAllFields}
              style={{ marginLeft: "auto", cursor: "pointer", fontSize: "20px" }}
            />
          </div>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Form>{renderDynamicForm()}</Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
          <Button
            variant="success"
            onClick={handleSendFormToExtension}
            disabled={sendingFormPayload}
          >
            Send to Extension
          </Button>
          <Button variant="primary" onClick={handleEdit}>Update</Button>
        </Modal.Footer>
      </Modal>

      {/* VIEW MODAL */}
      <Modal show={showView} onHide={() => setShowView(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Booking</Modal.Title>
          <FaCopy
            title="Copy All Fields"
            onClick={copyAllFields}
            style={{ marginLeft: "auto", cursor: "pointer", fontSize: "20px" }}
          />
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto", padding: "20px", background: "#f9f9f9", borderRadius: "6px" }}>
          {Object.entries(formData).map(([key, val]) => (
            <p key={key} style={{ marginBottom: "10px", wordBreak: "break-word", fontSize: "15px" }}>
              <strong style={{ width: "180px", display: "inline-block", color: "#333" }}>{key}:</strong> {val || "-"}
            </p>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowView(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete booking for <strong>{currentBooking.first_name} {currentBooking.last_name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </AppShell>
  );
};

export default AssistedBookings;
